import { useState, useEffect, useContext, forwardRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";

import "../InvoicesManagement.scss";
import { DataContext } from "../../utils/DataContext";
import { UserAuth } from "../../utils/AuthContext";
import useLanguage from "../../utils/useLanguage";
import { sanitizeNumber } from "../../utils/sanitizeNumber";

import CustomInput from "../../components/CustomInput";
import Dropdown from "../../components/Dropdown";
import CustomButton from "../../components/CustomButton";
import InformationBox from "../../components/InformationBox";
import BackToDashboard from "../../components/BackToDashboard";
import Loader from "../../components/Loader";
import RoundedTable from "../../components/table-component/RoundedTable";
import { TABLE_CELL_TYPES } from "../../components/table-component/RoundedTable";
import RoundedTableWithNesting from "./RoundedTableWithNesting";

import AddTreatmentModal from "./AddTreatmentModal";
import TreatmentInfoModal from "./TreatmentInfoModal";

import previousIcon from "../../assets/previous_icon.png";
import nextIcon from "../../assets/next_icon.png";
import addIconLight from "../../assets/add_icon_light.png";
import searchIcon from "../../assets/search_icon.png";
import infoIcon from "../../assets/information.png";

const TreatmentsPage = forwardRef((props, ref) => {
  // Context and hooks
  const { t } = useTranslation("general");
  const navigate = useNavigate();
  const { currentDirection } = useLanguage();
  const { userData } = useContext(UserAuth);

  const [loading, setLoading] = useState(false);

  // State variables
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTitle, setSelectedTitle] = useState({
    title: t("treatmentType"),
    value: "treatmentType",
  });
  const [treatments, setTreatments] = useState([]);
  const [treatmentsData, setTreatmentsData] = useState([]);
  const [showAddTreatmentModal, setShowAddTreatmentModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRecords: 0,
  });

  // Constants
  const tableTitles = [
    { value: "treatmentType" },
    { value: "price" },
    { value: "theOptions" },
  ];

  const titles = [
    { title: t("treatmentType"), value: "treatmentType" },
    { title: t("price"), value: "price" },
    { title: t("subTreatmentType"), value: "subMedicalTreatmentName" },
    { title: t("subTreatmentPrice"), value: "subMedicalTreatmentPrice" },
  ];

  // Handlers
  const handleViewInfoClick = (treatment) => {
    setSelectedTreatment(treatment);
    setShowInfoModal(true);
  };

  // Data fetching
  const getAllTreatments = async (page, searchQuery) => {
    try {
      const token = userData?.token;
      if (!token) {
        console.error("Token is missing, aborting request.");
        return;
      }

      const prices = ["price", "subMedicalTreatmentPrice"];

      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_URL}/getAllMedicalTreatments`,

        {
          params: {
            page: page,
            limit: 5,
            searchType: selectedTitle.value,
            searchQuery: prices.includes(selectedTitle?.value)
              ? sanitizeNumber(searchQuery)
              : searchQuery,

            organizationId: userData?.organizationId,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setTreatmentsData(response.data.data);
      const formattedTreatments = response.data.data.map((treatment) => ({
        hasSubcategories:
          treatment.subMedicalTreatment &&
          treatment.subMedicalTreatment.length > 0,
        data: [
          { value: treatment.name, type: TABLE_CELL_TYPES.TEXT },
          {
            value: treatment.treatmentPrice,
            type: TABLE_CELL_TYPES.MONEY,
            currency: `${t("IQD")}`,
          },
          {
            value: (
              <CustomButton
                variant="blank"
                title={t("ViewInfo")}
                onClick={() => handleViewInfoClick(treatment)}
                icon={infoIcon}
                iconSize={15}
              />
            ),
            type: TABLE_CELL_TYPES.ELEMENT,
          },
        ],
        subcategories: treatment.subMedicalTreatment?.map((treatment) => ({
          name: treatment.name,
          price: treatment.price,
          currency: `${t("IQD")}`,
          options: "-",
        })),
      }));

      setTreatments(formattedTreatments);
      setPagination({
        currentPage: page,
        totalPages: response?.data?.pagination?.pages,
        totalRecords: response?.data?.pagination?.total,
      });
    } catch (error) {
      console.error(
        "Error fetching treatments:",
        error.response?.data || error
      );
    } finally {
      setLoading(false);
    }
  };

  // Effects

  useEffect(() => {
    getAllTreatments(pagination.currentPage, searchQuery);
  }, [pagination.currentPage]);

  return (
    <>
      <main className="e-container">
        <BackToDashboard
          title={t("back")}
          variant="blank"
          icon={currentDirection === "ltr" ? previousIcon : nextIcon}
          iconSize={14}
          to="/appointments"
        />
        <div className="invoice-table-container">
          <div className="under-user-section">
            <div className="page-title">{t("treatments")}</div>
          </div>

          {loading ? (
            <Loader />
          ) : (
            <>
              <div className="top-action-buttons responsive-box">
                <div className="button-group">
                  <CustomButton
                    title={t("createTreatment")}
                    onClick={() => setShowAddTreatmentModal(true)}
                    icon={addIconLight}
                    iconSize={20}
                  />
                </div>
              </div>

              <div className="c-search-bar u-margin--bottom_500">
                <section className="c-search-bar-label-input">
                  <label>{t("search")}:</label>
                  <CustomInput
                    placeholder={t("search")}
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setPagination((prev) => ({ ...prev, currentPage: 1 }));
                    }}
                    resetSearch={() => {
                      setSearchQuery("");
                      setPagination((prev) => ({ ...prev, currentPage: 1 }));
                    }}
                    thick
                  />
                </section>

                <section className="c-search-bar-label-input">
                  {" "}
                  <label>{t("searchTitle")}: </label>
                  <Dropdown
                    options={titles}
                    selected={selectedTitle}
                    setSelected={(newTitle) => {
                      setSelectedTitle(newTitle);
                      setSearchQuery("");
                    }}
                    thick
                  />
                </section>
                <section className="col button-col">
                  <CustomButton
                    title={t("search")}
                    icon={searchIcon}
                    iconSize={15}
                    onClick={() => getAllTreatments(1, searchQuery)}
                  />
                </section>
              </div>

              {treatments.length > 0 ? (
                <RoundedTableWithNesting
                  titles={tableTitles}
                  rows={treatments}
                  pagination={pagination}
                  setPagination={setPagination}
                />
              ) : (
                <InformationBox>{t("noTreatmentsFound")}</InformationBox>
              )}
            </>
          )}
        </div>
      </main>

      {showAddTreatmentModal && (
        <AddTreatmentModal
          setShowAddTreatmentModal={setShowAddTreatmentModal}
          getAllTreatments={getAllTreatments}
        />
      )}

      {showInfoModal && selectedTreatment && (
        <TreatmentInfoModal
          treatment={selectedTreatment}
          onClose={() => setShowInfoModal(false)}
          getAllTreatments={getAllTreatments}
        />
      )}
    </>
  );
});

export default TreatmentsPage;
